import { CalendarDays, X } from "lucide-react";
import { useEffect, useId, useRef } from "react";
import { createPortal } from "react-dom";
import type { GarmentOrder } from "../types";

type GarmentSizeHistoryModalProps = {
  garmentName: string;
  orders: GarmentOrder[];
  onClose: () => void;
};

const dateFormatter = new Intl.DateTimeFormat("sv-SE", {
  year: "numeric",
  month: "long",
  day: "numeric",
});

function getOrderDate(date: number) {
  const orderDate = new Date(date);
  if (Number.isNaN(orderDate.getTime())) return null;
  return orderDate;
}

export function GarmentSizeHistoryModal({ garmentName, orders, onClose }: GarmentSizeHistoryModalProps) {
  const titleId = useId();
  const closeButtonRef = useRef<HTMLButtonElement>(null);

  useEffect(() => {
    const previouslyFocused = document.activeElement instanceof HTMLElement ? document.activeElement : null;
    document.body.classList.add("modal-open");
    closeButtonRef.current?.focus();

    function handleKeyDown(event: KeyboardEvent) {
      if (event.key === "Escape") onClose();
    }

    window.addEventListener("keydown", handleKeyDown);
    return () => {
      document.body.classList.remove("modal-open");
      window.removeEventListener("keydown", handleKeyDown);
      previouslyFocused?.focus();
    };
  }, [onClose]);

  return createPortal(
    <div
      className="size-history-backdrop"
      onMouseDown={(event) => {
        if (event.target === event.currentTarget) onClose();
      }}
    >
      <section className="size-history-modal" role="dialog" aria-modal="true" aria-labelledby={titleId}>
        <header className="size-history-modal__header">
          <div>
            <span className="eyebrow">Storlekshistorik</span>
            <h2 id={titleId}>{garmentName}</h2>
            <p>{orders.length} tidigare {orders.length === 1 ? "beställning" : "beställningar"}</p>
          </div>
          <button ref={closeButtonRef} className="icon-button" type="button" onClick={onClose} aria-label="Stäng storlekshistoriken">
            <X size={21} aria-hidden="true" />
          </button>
        </header>

        <div className="size-history-modal__list">
          {orders.map((order, index) => {
            const orderDate = getOrderDate(order.date);

            return (
              <article className="size-history-modal__order" key={`${order.date}-${order.name}-${index}`}>
                <div className="size-history-modal__person">
                  <strong>{order.name}</strong>
                  <span>
                    <CalendarDays size={14} aria-hidden="true" />
                    {orderDate ? (
                      <time dateTime={orderDate.toISOString()}>{dateFormatter.format(orderDate)}</time>
                    ) : (
                      "Datum saknas"
                    )}
                  </span>
                </div>
                <div className="size-history-modal__sizes" aria-label="Valda storlekar">
                  {Object.entries(order.sizes).map(([size, count]) => (
                    <span key={size}>{count > 1 ? `${count} × ` : ""}{size}</span>
                  ))}
                </div>
              </article>
            );
          })}
        </div>
      </section>
    </div>,
    document.body,
  );
}
